import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import type { PropsWithChildren } from 'react'
import React from 'react'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext)
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmProvider')
  }
  return ctx
}

interface ConfirmOptions {
  title?: string
  cancelText?: string
  description?: string
  confirmText?: string
}

type ConfirmContextType = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmContextType | null>(null)

export function ConfirmProvider (props: PropsWithChildren) {
  const [pendingState, setPendingState] = useState<{ options: ConfirmOptions; resolve: (value: boolean) => void } | null>(null)

  const [open, setOpen] = useState(false)

  const { t } = useTranslation()

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>(resolve => {
      setPendingState({ options, resolve })
    })
  }, [])

  useEffect(() => {
    if (pendingState) {
      setOpen(true)
    }
  }, [pendingState])

  const handleClose = (result: boolean) => {
    if (pendingState) {
      pendingState.resolve(result)
      setOpen(false)
      setTimeout(() => setPendingState(null), 200)
    }
  }

  return <ConfirmContext.Provider value={confirm}>
    {props.children}

    <Dialog open={open} onClose={() => handleClose(false)}>
      <DialogTitle>{t(pendingState?.options.title || 'Confirm')}</DialogTitle>
      <DialogContent>
        {t(pendingState?.options.description || 'Are you sure you want to continue?')}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose(false)}>
          {t(pendingState?.options.cancelText || 'Cancel')}
        </Button>
        <Button onClick={() => handleClose(true)} autoFocus>
          {t(pendingState?.options.confirmText || 'OK')}
        </Button>
      </DialogActions>
    </Dialog>
  </ConfirmContext.Provider>
}
